import { DownloadOutlined, LoadingOutlined } from '@ant-design/icons';
import {
  App,
  Button,
  Input,
  Modal,
  Result,
  Spin,
  Steps,
  Table,
  Tag,
  Timeline,
} from 'antd';
import {
  getIncidenceRequestDetail,
  updateIncidenceRequestStatus,
} from 'api/incidence-request.api';
import { PageTitle } from 'components/common/PageTitle/PageTitle';
import { dispatchIncidencePendingSummaryRefresh } from 'hooks/incidencePendingSummary';
import { useIsMobile } from 'hooks/useResponsive';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ApiError } from 'types';
import {
  IncidenceRequestApiStatus,
  IncidenceRequestStatus,
} from 'types/incidenceRequests';
import {
  EmployeeIncidenceRequestDetail,
  IncidenceRequestDetailContext,
  contextLabels,
  formatDisplayDate,
  getRequestStatusPresentation,
  getStatusEventPresentation,
  mapIncidenceRequestDetailRecord,
  shouldShowSteps,
  statusLabels,
} from './components/incidenceRequest.shared';
import { downloadIncidenceRequestAttachment } from './components/incidenceRequestAttachmentDownload';
import { IncidenceRequestReassignSupervisorModal } from './components/IncidenceRequestReassignSupervisorModal';
import 'styles/sass/incidence-requests.scss';
import { BackButton } from 'components/common/BackButton/BackButton';

type IncidenceRequestDetailPageProps = {
  context: IncidenceRequestDetailContext;
};

type DetailActionConfig = {
  key: 'approve' | 'forward' | 'authorize';
  label: string;
  status: IncidenceRequestApiStatus;
  successMessage: string;
  confirmTitle: string;
  confirmMessage: string;
};

const getErrorMessage = (error: unknown, fallbackMessage: string) => {
  if (error instanceof ApiError) {
    return error.message;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallbackMessage;
};

const getCurrentStep = (status: IncidenceRequestStatus) => {
  switch (status) {
    case 'draft':
      return 0;
    case 'review_supervisor':
      return 1;
    case 'review_hr':
    case 'wrong_supervisor':
      return 2;
    case 'approved':
    case 'rejected':
      return 3;
    default:
      return 0;
  }
};

const getStepItems = (status: IncidenceRequestStatus) => {
  const currentStep = getCurrentStep(status);
  const isRejected = status === 'rejected';

  return [
    { title: 'Borrador', description: '' },
    { title: 'En revision', description: 'Por Supervisor' },
    { title: 'En revision', description: 'Por RRHH' },
    { title: isRejected ? 'Rechazada' : 'Autorizada', description: '' },
  ].map((step, index) => {
    if (index < currentStep) {
      return { ...step, status: 'finish' as const };
    }

    if (index === currentStep) {
      return {
        ...step,
        status: (isRejected && index === 3 ? 'error' : 'process') as
          | 'error'
          | 'process',
      };
    }

    return { ...step, status: 'wait' as const };
  });
};

const buildDetailPath = (
  context: IncidenceRequestDetailContext,
  requestId: string
) => {
  if (context === 'mine') {
    return `/mis-solicitudes-incidencias/${requestId}`;
  }

  if (context === 'approvals') {
    return `/incidencias/aprobaciones/${requestId}`;
  }

  return `/incidencias/autorizaciones/${requestId}`;
};

export const IncidenceRequestDetailPage = ({
  context,
}: IncidenceRequestDetailPageProps) => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const mobileOnly = useIsMobile();
  const { message } = App.useApp();
  const [request, setRequest] = useState<EmployeeIncidenceRequestDetail | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [runningAction, setRunningAction] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<DetailActionConfig | null>(
    null
  );
  const [rejectionModalOpen, setRejectionModalOpen] = useState(false);
  const [reassignSupervisorModalOpen, setReassignSupervisorModalOpen] =
    useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [downloadingAttachmentKey, setDownloadingAttachmentKey] = useState<
    string | null
  >(null);

  const loadRequest = useCallback(async () => {
    if (!requestId) {
      setError('No se encontro la solicitud solicitada.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getIncidenceRequestDetail(requestId);
      setRequest(mapIncidenceRequestDetailRecord(response.data));
    } catch (loadError) {
      setRequest(null);
      setError(
        getErrorMessage(
          loadError,
          'No fue posible cargar el detalle de la solicitud.'
        )
      );
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    void loadRequest();
  }, [loadRequest]);

  const availableStatusActions = useMemo<DetailActionConfig[]>(() => {
    if (!request) {
      return [];
    }

    if (context === 'approvals' && request.status === 'review_supervisor') {
      return [
        {
          key: 'approve',
          label: 'Aprobar',
          status: 'EN_REVISION_RH',
          successMessage: 'La solicitud fue aprobada y enviada a RH.',
          confirmTitle: 'Aprobar solicitud',
          confirmMessage:
            'La solicitud avanzara a RH para su autorizacion final.',
        },
        {
          key: 'forward',
          label: 'Yo no soy su aprobador',
          status: 'WRONG_SUPERVISOR',
          successMessage:
            'La solicitud fue marcada con supervisor incorrecto y enviada a RH.',
          confirmTitle: 'Marcar supervisor incorrecto',
          confirmMessage:
            'La solicitud saldrá de tu bandeja y pasará a RH para que revisen que el supervisor asignado fue incorrecto.',
        },
      ];
    }

    if (
      context === 'authorizations' &&
      ['review_hr', 'wrong_supervisor'].includes(request.status)
    ) {
      return [
        {
          key: 'authorize',
          label: 'Autorizar',
          status: 'AUTORIZADA',
          successMessage: 'La solicitud fue autorizada.',
          confirmTitle: 'Autorizar solicitud',
          confirmMessage:
            'La solicitud quedara marcada como autorizada de forma definitiva.',
        },
      ];
    }

    return [];
  }, [context, request]);

  const canReject =
    (context === 'approvals' && request?.status === 'review_supervisor') ||
    (context === 'authorizations' &&
      ['review_hr', 'wrong_supervisor'].includes(request?.status ?? ''));
  const canAssignSupervisor =
    context === 'authorizations' &&
    request?.rhVisibleStatus === 'supervisor_incorrect' &&
    ['review_hr', 'wrong_supervisor'].includes(request?.status ?? '');

  const isDesktopOnlyContext = context !== 'mine';
  const useRhVisibleStatus = context === 'authorizations';
  const pageTitle = `${contextLabels[context]} | Detalle de solicitud`;
  const requestStatusPresentation = request
    ? getRequestStatusPresentation({
        status: request.status,
        rhVisibleStatus: request.rhVisibleStatus,
        useRhVisibleStatus,
      })
    : null;

  const downloadAttachment = async (
    attachment: EmployeeIncidenceRequestDetail['attachments'][number]
  ) => {
    if (!requestId) {
      message.error('No se encontró la solicitud solicitada.');
      return;
    }

    const attachmentKey = attachment.s3Key ?? attachment.name;

    try {
      setDownloadingAttachmentKey(attachmentKey);
      await downloadIncidenceRequestAttachment({
        requestId,
        attachment,
      });
    } catch (downloadError) {
      message.error(
        getErrorMessage(downloadError, 'No fue posible descargar el adjunto.')
      );
    } finally {
      setDownloadingAttachmentKey(null);
    }
  };

  const runStatusAction = async ({
    actionKey,
    status,
    successMessage,
    rejectionComment,
  }: {
    actionKey: string;
    status: IncidenceRequestApiStatus;
    successMessage: string;
    rejectionComment?: string;
  }) => {
    if (!requestId) {
      return;
    }

    try {
      setRunningAction(actionKey);
      await updateIncidenceRequestStatus(requestId, {
        status,
        ...(rejectionComment ? { rejectionReason: rejectionComment } : {}),
      });
      message.success(successMessage);
      await loadRequest();
      dispatchIncidencePendingSummaryRefresh();
    } catch (actionError) {
      message.error(
        getErrorMessage(
          actionError,
          'No fue posible actualizar el estatus de la solicitud.'
        )
      );
    } finally {
      setRunningAction(null);
    }
  };

  const historyColumns = [
    {
      title: 'Folio',
      dataIndex: 'folio',
      key: 'folio',
    },
    {
      title: 'Fecha inicial',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (value: string) => formatDisplayDate(value),
    },
    {
      title: 'Fecha final',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (value: string) => formatDisplayDate(value),
    },
    {
      title: 'Tipo de incidencia',
      dataIndex: 'incidenceType',
      key: 'incidenceType',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: IncidenceRequestStatus) => (
        <Tag
          className={`incidence-request-status incidence-request-status--${status}`}
        >
          {statusLabels[status]}
        </Tag>
      ),
    },
    {
      title: '',
      key: 'actions',
      render: (
        _: unknown,
        item: EmployeeIncidenceRequestDetail['history'][number]
      ) => (
        <Button
          type="link"
          onClick={() => navigate(buildDetailPath(context, item.id))}
          data-cy={`incidence-request-history-view-btn-${item.id}`}
        >
          Ver detalle
        </Button>
      ),
    },
  ];

  if (isDesktopOnlyContext && mobileOnly) {
    return (
      <>
        <PageTitle>{pageTitle}</PageTitle>
        <div className="incidence-request-detail-page">
          <Result
            status="warning"
            title="Vista disponible solo en escritorio"
            subTitle="Para revisar esta solicitud debes usar una computadora de escritorio."
            extra={
              <Button type="primary" onClick={() => navigate(-1)}>
                Regresar
              </Button>
            }
          />
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle>{pageTitle}</PageTitle>
      <div
        className="incidence-request-detail-page"
        data-cy={`incidence-request-detail-page-${context}`}
      >
        <h2 className="title">Detalle de solicitud</h2>
        <BackButton />
        <Spin
          spinning={loading}
          indicator={<LoadingOutlined />}
          tip="Cargando..."
        >
          {error ? (
            <Result
              status="error"
              title="No fue posible cargar la solicitud"
              subTitle={error}
              extra={
                <Button type="primary" onClick={() => void loadRequest()}>
                  Reintentar
                </Button>
              }
            />
          ) : null}

          {!error && request ? (
            <div className="incidence-request-detail-page-body">
              {shouldShowSteps(context) && (
                <Steps
                  className={`incidence-request-detail-steps ${mobileOnly ? 'incidence-request-detail-steps--mobile' : ''}`}
                  labelPlacement={mobileOnly ? 'vertical' : undefined}
                  progressDot={mobileOnly}
                  responsive={false}
                  current={getCurrentStep(request.status)}
                  items={getStepItems(request.status)}
                />
              )}

              <div className="incidence-request-detail-summary">
                <div className="incidence-request-detail-page-card">
                  <div className="incidence-request-detail-page-card-header">
                    <h2>Solicitud</h2>
                    <Tag
                      className={`incidence-request-status incidence-request-status--${requestStatusPresentation?.tone ?? request.status}`}
                    >
                      {requestStatusPresentation?.label ??
                        statusLabels[request.status]}
                    </Tag>
                  </div>
                  <div className="incidence-request-detail-grid">
                    <div className="incidence-request-detail-row">
                      <span className="incidence-request-detail-label">
                        Folio
                      </span>
                      <span className="incidence-request-detail-value">
                        {request.folio}
                      </span>
                    </div>
                    <div className="incidence-request-detail-row">
                      <span className="incidence-request-detail-label">
                        Tipo de incidencia
                      </span>
                      <span className="incidence-request-detail-value">
                        {request.incidenceType}
                      </span>
                    </div>
                    <div className="incidence-request-detail-row">
                      <span className="incidence-request-detail-label">
                        Motivo
                      </span>
                      <span className="incidence-request-detail-value">
                        {request.reason}
                      </span>
                    </div>
                    <div className="incidence-request-detail-row">
                      <span className="incidence-request-detail-label">
                        Fecha inicial
                      </span>
                      <span className="incidence-request-detail-value">
                        {formatDisplayDate(request.startDate)}
                      </span>
                    </div>
                    <div className="incidence-request-detail-row">
                      <span className="incidence-request-detail-label">
                        Fecha final
                      </span>
                      <span className="incidence-request-detail-value">
                        {formatDisplayDate(request.endDate)}
                      </span>
                    </div>
                    <div className="incidence-request-detail-row">
                      <span className="incidence-request-detail-label">
                        Fecha de solicitud
                      </span>
                      <span className="incidence-request-detail-value">
                        {formatDisplayDate(request.requestDate)}
                      </span>
                    </div>
                    <div className="incidence-request-detail-row incidence-request-detail-row--attachments">
                      <span className="incidence-request-detail-label">
                        Documentos adjuntos
                      </span>
                      <div className="incidence-request-detail-attachments">
                        {request.attachments.length ? (
                          request.attachments.map((attachment) => (
                            <div
                              className="incidence-request-detail-attachment-item"
                              key={attachment.s3Key ?? attachment.name}
                            >
                              <span className="incidence-request-detail-value">
                                {attachment.name}
                              </span>
                              <Button
                                type="text"
                                aria-label={`Descargar ${attachment.name}`}
                                className="incidence-request-detail-attachment-download"
                                icon={<DownloadOutlined />}
                                loading={
                                  downloadingAttachmentKey ===
                                  (attachment.s3Key ?? attachment.name)
                                }
                                onClick={() =>
                                  void downloadAttachment(attachment)
                                }
                              />
                            </div>
                          ))
                        ) : (
                          <span className="incidence-request-detail-empty">
                            Sin adjuntos
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {request.status === 'rejected' && (
                    <div className="incidence-request-detail-rejection-box">
                      <p className="incidence-request-detail-rejection-title">
                        Motivo de rechazo
                      </p>
                      <p className="incidence-request-detail-rejection-text">
                        {request.rejectionReason ??
                          'No se proporciono un motivo de rechazo.'}
                      </p>
                    </div>
                  )}
                </div>

                <div className="incidence-request-detail-page-card">
                  <div className="incidence-request-detail-page-card-header">
                    <h2>Solicitante y seguimiento</h2>
                  </div>
                  <div className="incidence-request-detail-grid">
                    <div className="incidence-request-detail-row">
                      <span className="incidence-request-detail-label">
                        Solicitante
                      </span>
                      <span className="incidence-request-detail-value">
                        {request.requester.fullName}
                      </span>
                    </div>
                    <div className="incidence-request-detail-row">
                      <span className="incidence-request-detail-label">
                        Numero de empleado
                      </span>
                      <span className="incidence-request-detail-value">
                        {request.requester.employeeNumber}
                      </span>
                    </div>
                    <div className="incidence-request-detail-row">
                      <span className="incidence-request-detail-label">
                        Correo
                      </span>
                      <span className="incidence-request-detail-value">
                        {request.requester.email}
                      </span>
                    </div>
                    <div className="incidence-request-detail-row">
                      <span className="incidence-request-detail-label">
                        Aprobador actual
                      </span>
                      <span className="incidence-request-detail-value">
                        {request.currentApprover?.fullName ?? '-'}
                      </span>
                    </div>
                    <div className="incidence-request-detail-row">
                      <span className="incidence-request-detail-label">
                        Aprobado por
                      </span>
                      <span className="incidence-request-detail-value">
                        {request.approvedBy?.fullName ?? '-'}
                      </span>
                    </div>
                    <div className="incidence-request-detail-row">
                      <span className="incidence-request-detail-label">
                        Autorizado por
                      </span>
                      <span className="incidence-request-detail-value">
                        {request.authorizedBy?.fullName ?? '-'}
                      </span>
                    </div>
                  </div>

                  {request.statusEvents.length ? (
                    <Timeline
                      className="incidence-request-detail-timeline"
                      items={request.statusEvents.map((event) => ({
                        children: (() => {
                          const eventStatusPresentation =
                            getStatusEventPresentation({
                              action: event.action,
                              fromStatus: event.fromStatus,
                              toStatus: event.toStatus,
                              useRhVisibleStatus,
                            });

                          return (
                            <div className="incidence-request-detail-event">
                              <p className="incidence-request-detail-event-title">
                                {eventStatusPresentation.label}
                              </p>
                              <p className="incidence-request-detail-event-copy">
                                {formatDisplayDate(event.performedAt)}
                                {event.performedBy
                                  ? ` · enviado por: ${event.performedBy.fullName}`
                                  : ''}
                              </p>
                              {event.comment ? (
                                <p className="incidence-request-detail-event-copy">
                                  {event.comment}
                                </p>
                              ) : null}
                            </div>
                          );
                        })(),
                      }))}
                    />
                  ) : null}
                </div>
              </div>

              {(availableStatusActions.length ||
                canReject ||
                canAssignSupervisor) && (
                <div className="incidence-request-detail-page-actions">
                  {canReject ? (
                    <Button
                      danger
                      onClick={() => {
                        setRejectionReason('');
                        setRejectionModalOpen(true);
                      }}
                      loading={runningAction === 'reject'}
                      data-cy="incidence-request-detail-reject-btn"
                    >
                      Rechazar
                    </Button>
                  ) : null}
                  {canAssignSupervisor ? (
                    <Button
                      onClick={() => setReassignSupervisorModalOpen(true)}
                      data-cy="incidence-request-detail-assign-supervisor-btn"
                    >
                      Asignar supervisor
                    </Button>
                  ) : null}
                  {availableStatusActions.map((action) => (
                    <Button
                      key={action.key}
                      type="primary"
                      onClick={() => setConfirmAction(action)}
                      loading={runningAction === action.key}
                      data-cy={`incidence-request-detail-${action.key}-btn`}
                    >
                      {action.label}
                    </Button>
                  ))}
                </div>
              )}

              <div className="incidence-request-detail-page-card">
                <div className="incidence-request-detail-page-card-header">
                  <h2>Historial</h2>
                </div>
                {mobileOnly ? (
                  <div className="incidence-request-history-mobile-list">
                    {request.history.length ? (
                      request.history.map((item) => (
                        <div
                          key={item.id}
                          className="incidence-request-history-mobile-item"
                        >
                          <div className="incidence-request-detail-row">
                            <span className="incidence-request-detail-label">
                              Folio
                            </span>
                            <span className="incidence-request-detail-value">
                              {item.folio}
                            </span>
                          </div>
                          <div className="incidence-request-detail-row">
                            <span className="incidence-request-detail-label">
                              Tipo
                            </span>
                            <span className="incidence-request-detail-value">
                              {item.incidenceType}
                            </span>
                          </div>
                          <div className="incidence-request-detail-row">
                            <span className="incidence-request-detail-label">
                              Fecha inicial
                            </span>
                            <span className="incidence-request-detail-value">
                              {formatDisplayDate(item.startDate)}
                            </span>
                          </div>
                          <div className="incidence-request-detail-row">
                            <span className="incidence-request-detail-label">
                              Fecha final
                            </span>
                            <span className="incidence-request-detail-value">
                              {formatDisplayDate(item.endDate)}
                            </span>
                          </div>
                          <div className="incidence-request-detail-row">
                            <span className="incidence-request-detail-label">
                              Status
                            </span>
                            <Tag
                              className={`incidence-request-status incidence-request-status--${item.status}`}
                            >
                              {statusLabels[item.status]}
                            </Tag>
                          </div>
                          <Button
                            type="link"
                            onClick={() =>
                              navigate(buildDetailPath(context, item.id))
                            }
                            data-cy={`incidence-request-history-mobile-view-btn-${item.id}`}
                          >
                            Ver detalle
                          </Button>
                        </div>
                      ))
                    ) : (
                      <p className="incidence-request-detail-empty">
                        No hay historial disponible.
                      </p>
                    )}
                  </div>
                ) : (
                  <Table
                    rowKey="id"
                    columns={historyColumns}
                    dataSource={request.history}
                    pagination={false}
                    className="table-ps"
                    bordered
                    locale={{
                      emptyText: 'No hay historial disponible.',
                    }}
                  />
                )}
              </div>
            </div>
          ) : null}
        </Spin>
      </div>

      <Modal
        open={Boolean(confirmAction)}
        title={confirmAction?.confirmTitle}
        okText={confirmAction?.label}
        cancelText="Cancelar"
        onCancel={() => setConfirmAction(null)}
        onOk={() => {
          if (!confirmAction) {
            return;
          }

          void runStatusAction({
            actionKey: confirmAction.key,
            status: confirmAction.status,
            successMessage: confirmAction.successMessage,
          });
          setConfirmAction(null);
        }}
      >
        <p>{confirmAction?.confirmMessage}</p>
      </Modal>

      <IncidenceRequestReassignSupervisorModal
        open={reassignSupervisorModalOpen}
        requestId={requestId}
        requester={request?.requester}
        onCancel={() => setReassignSupervisorModalOpen(false)}
        onSuccess={({ record, supervisorLabel }) => {
          setReassignSupervisorModalOpen(false);
          setRequest(mapIncidenceRequestDetailRecord(record));
          message.success(
            `${supervisorLabel} será el nuevo supervisor del empleado de ahora en adelante.`
          );
        }}
      />

      <Modal
        open={rejectionModalOpen}
        title="Rechazar solicitud"
        okText="Rechazar"
        okButtonProps={{
          danger: true,
          disabled: !rejectionReason.trim(),
          loading: runningAction === 'reject',
        }}
        cancelText="Cancelar"
        onCancel={() => setRejectionModalOpen(false)}
        onOk={() => {
          const trimmedReason = rejectionReason.trim();
          if (!trimmedReason) {
            return;
          }

          void runStatusAction({
            actionKey: 'reject',
            status: 'RECHAZADA',
            successMessage: 'La solicitud fue rechazada.',
            rejectionComment: trimmedReason,
          });
          setRejectionModalOpen(false);
        }}
      >
        <p className="incidence-request-detail-reject-modal-copy">
          El motivo de rechazo es obligatorio para continuar.
        </p>
        <Input.TextArea
          rows={4}
          value={rejectionReason}
          onChange={(event) => setRejectionReason(event.target.value)}
          placeholder="Escribe el motivo de rechazo"
          data-cy="incidence-request-detail-rejection-reason"
        />
      </Modal>
    </>
  );
};
